import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { Link } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { GoogleLoginButton } from "@/lib/auth";

const LINKS = [
  { label: "Formules", href: "#formules" },
  { label: "Méthode", href: "#methode" },
  { label: "Playlist", href: "#playlist" },
  { label: "Galerie", href: "#galerie" },
  { label: "Infos pratiques", href: "#infos" },
];

export default function Header() {
  const lenis = useLenis();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    lenis?.scrollTo(href, { offset: -72, duration: 1.4 });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-[90] transition-[background-color,border-color] duration-500 ${
        open
          ? "border-b border-cyan-500/15 bg-[#080C14]"
          : scrolled
            ? "border-b border-cyan-500/15 bg-[#080C14]/80 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="header-logo-button"
          onClick={() => lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center"
          aria-label="KLMProd — retour en haut"
        >
          <img src="/logo-v2.png" alt="KLMProd" className="h-9 w-auto" />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-cyan-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 to-blue-600 transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <Link
            to="/playlists"
            data-testid="nav-link-playlists"
            className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-cyan-300"
          >
            Playlists
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 to-blue-600 transition-[width] duration-300 group-hover:w-full" />
          </Link>
          <Link
            to="/tarifs"
            data-testid="nav-link-tarifs"
            className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-cyan-300"
          >
            Tarifs
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 to-blue-600 transition-[width] duration-300 group-hover:w-full" />
          </Link>
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-cyan-300"
          >
            Blog
            <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-cyan-400 to-blue-600 transition-[width] duration-300 group-hover:w-full" />
          </Link>
          {user ? (
            <div className="flex items-center gap-2 rounded-full border border-cyan-500/20 bg-[#0E1524]/70 py-1 pl-1 pr-3" data-testid="header-user-chip">
              {user.picture ? (
                <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" referrerPolicy="no-referrer" />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan-400/20 text-xs font-bold text-cyan-300">
                  {user.name?.[0]?.toUpperCase()}
                </span>
              )}
              <span className="max-w-[120px] truncate text-xs font-semibold text-slate-200">{user.name}</span>
              <button
                data-testid="header-logout-button"
                onClick={logout}
                title="Se déconnecter"
                aria-label="Se déconnecter"
                className="text-slate-400 transition-colors hover:text-red-300"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <GoogleLoginButton testId="header-login-button" label="Se connecter" className="px-4 py-2 text-xs" />
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="nav-link-admin"
              className="text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-cyan-300"
            >
              Admin
            </Link>
          )}
          <button
            data-testid="header-cta-devis"
            onClick={() => go("#devis")}
            className="bg-brand-gradient rounded-full px-5 py-2.5 text-sm font-bold text-[#04121f] transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            Demander un devis
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="text-slate-200 lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="fixed inset-x-0 bottom-0 top-[72px] z-[85] overflow-y-auto bg-[#080C14] px-6 pb-8 pt-2 lg:hidden" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`mobile-nav-link-${l.href.slice(1)}`}
              onClick={() => go(l.href)}
              className="block w-full py-3 text-left text-base font-medium text-slate-200"
            >
              {l.label}
            </button>
          ))}
          <Link
            to="/playlists"
            data-testid="mobile-nav-link-playlists"
            onClick={() => setOpen(false)}
            className="block w-full py-3 text-left text-base font-medium text-slate-200"
          >
            Playlists
          </Link>
          <Link
            to="/tarifs"
            data-testid="mobile-nav-link-tarifs"
            onClick={() => setOpen(false)}
            className="block w-full py-3 text-left text-base font-medium text-slate-200"
          >
            Tarifs
          </Link>
          <Link
            to="/blog"
            data-testid="mobile-nav-link-blog"
            onClick={() => setOpen(false)}
            className="block w-full py-3 text-left text-base font-medium text-slate-200"
          >
            Blog
          </Link>
          {user?.is_admin && (
            <Link
              to="/admin"
              data-testid="mobile-nav-link-admin"
              onClick={() => setOpen(false)}
              className="block w-full py-3 text-left text-base font-medium text-slate-200"
            >
              Admin
            </Link>
          )}
          {user ? (
            <div className="flex items-center justify-between py-3" data-testid="mobile-user-chip">
              <span className="text-sm font-semibold text-slate-200">{user.name}</span>
              <button
                data-testid="mobile-logout-button"
                onClick={logout}
                className="flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-red-300"
              >
                <LogOut size={14} /> Déconnexion
              </button>
            </div>
          ) : (
            <GoogleLoginButton testId="mobile-login-button" className="mt-2 w-full" />
          )}
          <button
            data-testid="mobile-cta-devis"
            onClick={() => go("#devis")}
            className="bg-brand-gradient mt-2 w-full rounded-full px-5 py-3 text-sm font-bold text-[#04121f]"
          >
            Demander un devis
          </button>
        </div>
      )}
    </header>
  );
}
